import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ShoppingBag, Search, MoreHorizontal, Eye, Power } from 'lucide-react';
import { adminApi, resolveUrl } from '@/services/adminApi';
import type { AdminProductListItem, AdminProductsResponse } from '@/services/adminApi';
import { queryKeys } from '@/lib/queryKeys';
import { formatPrice } from '@printtat/shared';
import { PageHeader, PageContent, Tabs, EmptyState, LoadingState, Button, Table } from '@printtat/ui';
import type { Column, SortState } from '@printtat/ui';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@printtat/ui';

type TabKey = 'all' | 'active' | 'inactive';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'inactive', label: 'Inactive' },
];

const formatDate = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export const AdminProductsPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [tab, setTab] = useState<TabKey>('all');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortState>({ key: 'createdAt', order: 'desc' });

  const limit = 20;

  const isActiveFilter = tab === 'active' ? true : tab === 'inactive' ? false : undefined;

  const { data, isLoading } = useQuery<AdminProductsResponse>({
    queryKey: [...queryKeys.products.all(), { search, isActive: isActiveFilter, page, limit, ...sort }],
    queryFn: () => adminApi.products.list({
      search: search || undefined,
      isActive: isActiveFilter,
      page,
      limit,
      sortBy: sort.key,
      sortOrder: sort.order,
    }),
  });

  const toggleActive = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      adminApi.products.updateProduct(id, { isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'products'] });
    },
  });

  const products = data?.products ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / limit);

  const columns: Column<AdminProductListItem>[] = [
    {
      key: 'product',
      header: 'Product',
      render: (p) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center shrink-0 overflow-hidden">
            {p.primaryImage ? (
              <img src={resolveUrl(p.primaryImage)} alt="" crossOrigin="anonymous" className="w-10 h-10 rounded-lg object-cover" />
            ) : (
              <ShoppingBag size={16} className="text-gray-400" />
            )}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{p.name}</p>
            <p className="text-xs text-gray-500 truncate">{p.category}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'basePrice',
      header: 'Base Price',
      sortable: true,
      render: (p) => <span className="text-sm text-gray-700">{formatPrice(p.basePrice)}</span>,
    },
    {
      key: 'variantCount',
      header: 'Variants',
      sortable: true,
      render: (p) => (
        <span className="text-sm text-gray-600">
          {p.activeVariantCount} / {p.totalVariantCount}
        </span>
      ),
    },
    {
      key: 'totalStock',
      header: 'Stock',
      sortable: true,
      render: (p) => (
        <span className={`text-sm ${p.totalStock === 0 ? 'text-red-500 font-medium' : 'text-gray-600'}`}>
          {p.totalStock}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (p) => (
        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
          p.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
        }`}>
          {p.isActive ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      key: 'createdAt',
      header: 'Created',
      sortable: true,
      render: (p) => <span className="text-sm text-gray-500">{formatDate(p.createdAt)}</span>,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      className: 'w-10',
      render: (p) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="p-1 rounded hover:bg-gray-100 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal size={16} className="text-gray-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => navigate(`/products/${p.id}`)}>
              <Eye size={14} className="mr-2" />
              View details
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => toggleActive.mutate({ id: p.id, isActive: !p.isActive })}
            >
              <Power size={14} className="mr-2" />
              {p.isActive ? 'Deactivate' : 'Activate'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <PageContent className="overflow-y-auto h-screen">
      <PageHeader
        icon={ShoppingBag}
        title="Products"
        description={`${total} total product${total !== 1 ? 's' : ''}`}
      />

      {/* Tabs */}
      <div className="mt-6">
        <Tabs
          tabs={TABS}
          value={tab}
          onChange={(t) => { setTab(t); setPage(1); }}
          variant="pill"
        />
      </div>

      {/* Filters */}
      <div className="mt-4 flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search products..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-gray-200"
          />
        </div>
      </div>

      {/* Products table */}
      <div className="mt-4">
        {isLoading ? (
          <LoadingState variant="skeleton" columns={1} rows={5} />
        ) : products.length === 0 ? (
          <EmptyState
            icon={ShoppingBag}
            title="No products found"
            description={search ? 'Try adjusting your search' : undefined}
          />
        ) : (
          <>
            <Table
              columns={columns}
              data={products}
              keyExtractor={(p) => p.id}
              onRowClick={(p) => navigate(`/products/${p.id}`)}
              sort={sort}
              onSortChange={(s) => { setSort(s); setPage(1); }}
            />

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <span className="text-sm text-gray-500">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </PageContent>
  );
};
